import { VariantSelectedEvent, VariantUpdateEvent, ThemeEvents } from '@theme/events';

/** @type {Map<string, Promise<object>>} */
const productJsonPromises = new Map();

/**
 * @param {string} handle
 */
function fetchProductJson(handle) {
  if (productJsonPromises.has(handle)) return /** @type {Promise<object>} */ (productJsonPromises.get(handle));

  const promise = fetch(`/products/${encodeURIComponent(handle)}.js`).then((r) => r.json());
  productJsonPromises.set(handle, promise);
  return promise;
}

/**
 * @param {object} product
 * @param {string | undefined} currentVariantId
 * @param {number} optionPosition 1-based
 * @param {string} sizeLabel
 */
function resolveVariantId(product, currentVariantId, optionPosition, sizeLabel) {
  const variants = product?.variants;
  if (!variants?.length) return undefined;

  const keys = /** @type {const} */ (['option1', 'option2', 'option3']);
  const posIdx = optionPosition - 1;
  if (posIdx < 0 || posIdx > 2) return undefined;

  const current = variants.find((v) => String(v.id) === String(currentVariantId));

  const match = variants.find((v) => {
    if (v[keys[posIdx]] !== sizeLabel) return false;
    if (!current) return true;
    for (let i = 0; i < 3; i++) {
      if (i === posIdx) continue;
      if (v[keys[i]] !== current[keys[i]]) return false;
    }
    return true;
  });

  return match ? String(match.id) : undefined;
}

/**
 * Size pills on product cards: no PDP navigation; ATC uses the resolved variant.
 */
class ProductCardSizesComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {HTMLElement | null | undefined} */
  #productCard;

  /** @type {(event: Event) => void} */
  #onVariantUpdateOnCard = () => {
    requestAnimationFrame(() => void this.#syncSelectionFromForm());
  };

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#productCard = this.closest('product-card');
    this.#productCard?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateOnCard);
    void this.#syncSelectionFromForm();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#productCard?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateOnCard);
    this.#productCard = null;
  }

  async #syncSelectionFromForm() {
    const card = this.closest('product-card');
    const input = card?.querySelector('product-form-component input[name="id"]');
    const vid = input?.value;
    const handle = this.dataset.productHandle;
    const pos = Number.parseInt(this.dataset.optionPosition || '', 10);

    if (!vid || !handle || !Number.isFinite(pos)) return;

    let product;
    try {
      product = await fetchProductJson(handle);
    } catch {
      return;
    }

    const variant = product.variants?.find((v) => String(v.id) === vid);
    if (!variant) return;

    const keys = /** @type {const} */ (['option1', 'option2', 'option3']);
    const labelAtPos = variant[keys[pos - 1]];

    for (const btn of this.querySelectorAll('button[data-option-label]')) {
      const match = btn.dataset.optionLabel === labelAtPos;
      btn.classList.toggle('product-card-sizes__link--selected', match);
      btn.setAttribute('aria-pressed', match ? 'true' : 'false');
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = async (event) => {
    const button = event.target.closest('button[data-option-label]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const handle = this.dataset.productHandle;
    const optionPosition = Number.parseInt(this.dataset.optionPosition || '', 10);
    const optionValueId = button.dataset.optionValueId;
    const sizeLabel = button.dataset.optionLabel;
    const card = this.closest('product-card');
    const link = card?.querySelector('[ref="productCardLink"]');

    if (!handle || !Number.isFinite(optionPosition) || !optionValueId || !sizeLabel || !link?.href) return;

    const formInput = card.querySelector('product-form-component input[name="id"]');
    const currentVariantId = formInput?.value;

    let product;
    try {
      product = await fetchProductJson(handle);
    } catch (e) {
      console.error(e);
      return;
    }

    const variantId =
      resolveVariantId(product, currentVariantId, optionPosition, sizeLabel) || button.dataset.variantId;

    if (!variantId) return;

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', 'section-rendering-product-card');

    this.#abort?.abort();
    this.#abort = new AbortController();

    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId }));

    try {
      const responseText = await fetch(url.toString(), { signal: this.#abort.signal }).then((r) => r.text());

      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const textContent = doc.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!textContent) return;

      let variant;
      try {
        variant = JSON.parse(textContent);
      } catch {
        return;
      }

      this.dispatchEvent(
        new VariantUpdateEvent(variant, optionValueId, {
          html: doc,
          productId: this.dataset.productId ?? '',
        })
      );

      await this.#syncSelectionFromForm();
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err);
    }
  };
}

if (!customElements.get('product-card-sizes-component')) {
  customElements.define('product-card-sizes-component', ProductCardSizesComponent);
}
